import React, { useEffect, useState } from 'react'
import { Layout, Stack, Main, Sidebar } from '@layout'
import Divider from '@components/Divider'
import { Card as CardComponent, Box, Heading, Text, Image } from 'theme-ui'
import { readingTime } from 'reading-time-estimator'
import { PostHead, PostBody } from '@widgets/Post'
import Section from '@components/Section'
import MemphisPattern from '@components/MemphisPattern'

// Client-only handler for /blog
// - If ?slug=...&mode=preview is present: render the single blog (draft or published) from Strapi
// - Otherwise: redirect to the existing blogs listing route (/blogs)
export default function BlogEntryOrListing(props) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [post, setPost] = useState(null)

  const normalizeSlug = value => {
    if (!value) return null

    const decoded = decodeURIComponent(value).trim().replace(/\/+$/, '')
    const blogMatch = decoded.match(/^\/blog\/([^/]+)$/i)
    if (blogMatch) {
      return blogMatch[1]
    }

    return decoded.replace(/^\//, '')
  }

  const PreviewAuthors = ({ authors }) => {
    if (!authors || authors.length === 0) return null

    const PreviewAvatar = ({ src, alt }) => {
      if (!src) return null

      const avatarSize = 140
      const ringSize = 150
      const canvaswidth = 210
      const canvashight = 170

      return (
        <Box sx={{ position: 'relative', width: canvaswidth, height: canvashight, mx: 'auto' }}>
          <MemphisPattern sx={{ backgroundSize: '8rem', opacity: 0.17 ,borderRadius: 'lg', overflow: 'hidden'}} />
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: 10,
              transform: 'translateX(-50%)',
              width: ringSize,
              height: ringSize,
              borderRadius: '9999px',
              zIndex: 2,
              ml: '-0.7rem',
              bg: 'transparent',
              boxShadow: t => `0.7rem 1.4rem ${t.colors.omegaLight}`
            }}
          />
          <Image
            src={src}
            alt={alt}
            sx={{
              position: 'absolute',
              left: '50%',
              top: 18,
              transform: 'translateX(-50%) translate(-5%, 1%)',
              width: avatarSize,
              height: avatarSize,
              objectFit: 'cover',
              borderRadius: '9999px'
            }}
          />
        </Box>
      )
    }

    return (
      <Section aside title='The Author' omitTitle>
        {authors.map(author => {
          return (
            <CardComponent key={author.id || author.name} variant='paper'>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 4 }}>
                  <PreviewAvatar src={author.thumbnailUrl} alt={author.name} />
                </Box>

                <Heading variant='h3'>{author.name}</Heading>
                {author.title && (
                  <Heading variant='h4' sx={{ color: 'omegaDark' }}>
                    {author.title}
                  </Heading>
                )}
                {author.authorDescription && (
                  <Box sx={{ textAlign: 'left', lineHeight: '1.5', mt: 3 }}>
                    <div dangerouslySetInnerHTML={{ __html: author.authorDescription }} />
                  </Box>
                )}
              </Box>
            </CardComponent>
          )
        })}
      </Section>
    )
  }

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search)
      const slug = normalizeSlug(params.get('slug'))
      const documentId = params.get('documentId')
      const mode = params.get('mode')

      if (!slug) {
        setError('Missing slug parameter')
        setLoading(false)
        return
      }

      if (mode !== 'preview') {
        setError('Preview mode is required for this page')
        setLoading(false)
        return
      }

      try {
        const apiBase = process.env.GATSBY_STRAPI_API_URL
        const token = process.env.GATSBY_TOKEN
        if (!apiBase || !token) {
          setError('Missing GATSBY_STRAPI_API_URL or GATSBY_TOKEN env')
          setLoading(false)
          return
        }

        const toAbsUrl = url => {
          if (!url) return null
          if (url.startsWith('http://') || url.startsWith('https://')) return url
          if (url.startsWith('/') && apiBase) return `${apiBase}${url}`
          return url
        }

        // Strapi 5: avoid populate[field]=* on media (causes "Invalid key related" 400)
        const populate =
          'populate=featuredImage&populate=categories&populate[authors][populate]=thumbnail'
        const slugFilter = `filters[slug][$eq]=${encodeURIComponent(slug)}`
        const documentIdFilter = documentId
          ? `filters[documentId][$eq]=${encodeURIComponent(documentId)}`
          : null

        const fetchBlog = async ({ status, useDocumentId = false } = {}) => {
          const statusParam = status ? `&status=${status}` : ''
          const filterParam = useDocumentId && documentIdFilter ? documentIdFilter : slugFilter
          const res = await fetch(
            `${apiBase}/api/blogs?${filterParam}&${populate}${statusParam}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          if (!res.ok) throw new Error(`Preview fetch failed: ${res.status}`)
          const json = await res.json()
          return json?.data?.[0]
        }

        let item = documentId ? await fetchBlog({ status: 'draft', useDocumentId: true }) : null
        if (!item) {
          item = await fetchBlog({ status: 'draft' })
        }
        if (!item && documentId) {
          item = await fetchBlog({ useDocumentId: true })
        }
        if (!item) {
          item = await fetchBlog()
        }
        if (!item) {
          setError(`No blog entry found for slug "${slug}"`)
          setLoading(false)
          return
        }

        const a = item.attributes || item || {}
        const title = a.title || 'Untitled'
        const rawCategories = a.categories?.data || a.categories || []
        const categories = rawCategories
          .map(c => c?.attributes || c)
          .filter(Boolean)
          .map(c => ({ name: c.name, slug: c.slug }))

        const rawAuthors = a.authors?.data || a.authors || []
        const authors = rawAuthors
          .map(x => {
            const at = x?.attributes || {}
            const normalizedAuthor = Object.keys(at).length > 0 ? at : x || {}
            const normalizedThumbnail =
              normalizedAuthor.thumbnail?.data?.attributes?.url ||
              normalizedAuthor.thumbnail?.url
            return {
              id: x?.id,
              name: normalizedAuthor.name,
              slug: normalizedAuthor.slug,
              title: normalizedAuthor.title,
              institution: normalizedAuthor.institution,
              authorDescription: normalizedAuthor.authorDescription,
              thumbnailUrl: toAbsUrl(normalizedThumbnail)
            }
          })
          .filter(x => x && x.name)

        let imageUrl = a.featuredImage?.data?.attributes?.url || a.featuredImage?.url
        imageUrl = toAbsUrl(imageUrl)
        const bodyHtml = a.body?.data?.body || a.body || a.content || ''

        const date = a.date
          ? new Date(a.date).toLocaleDateString('en-us', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          : ''

        const text = bodyHtml.replace(/(<([^>]+)>)/gi, '')
        const rt = readingTime(text || '', 220)
        const timeToRead = rt?.minutes ? Math.ceil(rt.minutes) : null

        setPost({
          title,
          categories,
          authors,
          date,
          timeToRead,
          readingTime: rt,
          imageUrl,
          bodyContent: bodyHtml
        })
        setLoading(false)
      } catch (err) {
        setError(err.message)
        setLoading(false)
      }
    }
    run()
  }, [])

  return (
    <Layout {...props}>
      <Box style={{ paddingTop: '70px' }}>
        {loading && (
          <Box sx={{ maxWidth: 980, mx: 'auto', px: 3, py: 4 }}>
            <Text>Loading preview…</Text>
          </Box>
        )}
        {error && (
          <Box sx={{ maxWidth: 980, mx: 'auto', px: 3, py: 4, color: 'red' }}>
            <Heading as='h2' sx={{ mb: 2 }}>
              Preview error
            </Heading>
            <Text>{error}</Text>
          </Box>
        )}
        {!loading && !error && post && (
          <>
            <Stack effectProps={{ effect: 'fadeInDown' }}>
              <PostHead
                title={post.title}
                authorsOverride={post.authors}
                categories={post.categories}
                date={post.date}
                timeToRead={post.timeToRead}
                readingTime={post.readingTime}
                name='blogPage'
              />
            </Stack>

            <Stack effectProps={{ fraction: 0 }}>
              <Main>
                <Divider />
                <CardComponent variant='paper'>
                  {post.imageUrl && (
                    <>
                      <Box
                        sx={{
                          mt: -4,
                          mx: -4,
                          minHeight: '23rem',
                          height: '23rem',
                          overflow: 'hidden',
                          borderRadius: t => `${t.radii.lg} ${t.radii.lg} 0 0`
                        }}
                      >
                        <Image
                          src={post.imageUrl}
                          alt={post.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: t => `${t.radii.lg} ${t.radii.lg} 0 0`
                          }}
                        />
                      </Box>
                      <Divider space={3} />
                    </>
                  )}
                  <PostBody bodyContent={post.bodyContent} />
                </CardComponent>
              </Main>

              <Sidebar>
                <Divider />
                <PreviewAuthors authors={post.authors} />
                <Divider />
              </Sidebar>
            </Stack>
          </>
        )}
      </Box>
    </Layout>
  )
}
